import {Component, OnDestroy, OnInit} from '@angular/core';
import {GridOptions, RowClassRules} from 'ag-grid-community';
import {ActivatedRoute, Router} from '@angular/router';
import {InstrumentGridModel} from '../../../core/models/InstrumentGridModel';
import {DataService} from '../../../core/services/data.service';
import {CellRenderersService} from '../../../core/services/cellRenderers.service';
import {UtilityService} from '../../../core/services/utility.service';
import {Store} from '../../../store';
import {User} from '../../../core/models/User';
import {QueryConfig} from '../../../core/models/QueryConfig';
import {Observable, Subject, combineLatest} from 'rxjs';
import {ServiceRequest} from '../../../core/models/ServiceRequest';
import * as _ from 'underscore';
import {ApplicationEventsService} from '../../../core/services/application-events.service';
import { takeUntil, distinctUntilChanged } from 'rxjs/operators';

@Component({
    selector: 'app-counter-party',
    templateUrl: './counter-party.component.html',
    styleUrls: ['./counter-party.component.scss'],
    standalone: false
})
export class CounterPartyComponent implements OnDestroy, OnInit {
    public unsubscribe: Subject<void> = new Subject();

    public entityName: string;
    public entityId: string;
    public counterPartyName: string;
    counterPartyCcrEceChartData: any[];
    derivativesVsCollateralChartData: any[];
    user: User;
    currentQueryConfig = <QueryConfig>{
        rolloverToCash: true,
        setForGroup: false,
        entityTailConfigs: undefined,
        groupTailConfig: undefined
    };
    instrumentGridData: InstrumentGridModel[];
    wccrPeriodGridData: any[];
    instrumentGridColumnDef: any[];
    wccrPeriodGridColumnDef: any[];

    overlayLoadingTemplate: string;
    overlayNoRowsTemplate: string;

    instrumentGridCollapse = false;
    components: any;

    instrumentGridOptions = <GridOptions>{};

    public instrumentGridApi;
    public wccrPeriodGridApi;
    domLayout: 'normal' | 'autoHeight' | 'print' = 'autoHeight';

  
    rowClassRules:RowClassRules  = {
        'last-child-column': (params) => {
            return params.data.isLastChildColumn? true: false;
          }
    };

    instrumentGridToolBarOption = {
        horizon: {
            text: 'Now to 1 years',
            from: 0,
            to: 12
        },
        instrumentType: '-1',
        groupBy: 'derivative',
        searchText: ''
    };

    selectedCcrEceChartOption = '12';
    selectedDerivativesCollateralChartOption = '12';
    ccrTimeStepClickFilter: any = null;

    ngOnInit() {
        combineLatest(
            this.route.params,
            this.store.select<User>('user').pipe(distinctUntilChanged((prev, curr) => _.isEqual(prev, curr)))
        ).subscribe(([params, user]) => {
            this.entityId = params.entityId;
            this.counterPartyName = params.counterPartyId;

            this.user = user;
            this.entityName = this.computeEntityName(this.entityId, this.user);
            this.resetPageData();
            this.reloadPage();
        });

        this.components = {simpleCellRenderer: this.getSimpleCellRenderer()};
        this.subscribeOnPageReload();
    }

    constructor(private dataService: DataService, private route: ActivatedRoute, private router: Router, private store: Store,
                private cellRenderersService: CellRenderersService, private utilityService: UtilityService,
                private appEvents: ApplicationEventsService) {

        this.overlayLoadingTemplate = this.utilityService.overlayLoadingTemplate;
        this.overlayNoRowsTemplate = this.utilityService.overlayNoRowsTemplate;

        this.instrumentGridColumnDef = [
            {
                headerName: 'Type',
                field: 'type'
            }, {
                headerName: 'Product',
                field: 'product'
            }, {
                headerName: 'CounterParty',
                field: 'counterParty'
            }, {
                headerName: 'ID',
                field: 'instrumentId'
            }, {
                headerName: 'CCY',
                field: 'currency'
            }, {
                headerName: 'Notional',
                field: 'notional'
            }, {
                headerName: 'Value today',
                field: 'valueToday'
            }, {
                headerName: 'Min Val',
                field: 'minVal'
            }, {
                headerName: 'Min Val Date',
                field: 'minValDate'
            }, {
                headerName: 'Max Val',
                field: 'maxVal'
            }, {
                headerName: 'Max Val Date',
                field: 'maxValDate'
            }, {
                headerName: 'MV (Tail %)',
                field: 'valDiff'
            }, {
                headerName: 'Date MV',
                field: 'valDiffDate'
            }, {
                headerName: 'Expiry',
                field: 'expiry'
            }
        ];

        this.wccrPeriodGridColumnDef = [
            {
                headerName: 'Starts',
                field: 'start'
            }, {
                headerName: 'Ends',
                field: 'end'
            }, {
                headerName: 'Dur',
                field: 'duration'
            }, {
                headerName: 'CCR',
                field: 'ccrPercentageString'
            }, {
                headerName: 'Cash ECE',
                field: 'cashEceInMUsd'
            }, {
                headerName: 'Bond ECE',
                field: 'bondEceInMUsd'
            }
        ];
    }

    public computeEntityName(id: string, user: User): string {
        if (!user) {
            return null;
        }
        for (const entity of  user.legalEntities) {
            if (entity.entityId === id) {
                return entity.dataName;
            }
        }
        return null;
    }

    public reloadPage() {
        this.setCurrentQueryConfig();
        this.loadPageData();
    }

    public setCurrentQueryConfig() {
        this.currentQueryConfig.rolloverToCash = this.user.queryConfig.rolloverToCash;
        this.currentQueryConfig.setForGroup = this.user.queryConfig.setForGroup;
        this.currentQueryConfig.groupTailConfig = this.user.queryConfig.groupTailConfig;
        this.currentQueryConfig.entityTailConfigs = this.user.queryConfig.entityTailConfigs;
    }

    public loadPageData() {
        this.getCcrAndEceData();
        this.getCounterPartyDerivativeAndCollateral();
        this.getInstruments();
    }

    public getCcrAndEceData() {
        this.dataService.getEntityCounterPartyCcrAndEce(this.entityName, this.counterPartyName,
            this.currentQueryConfig.setForGroup ? this.currentQueryConfig.groupTailConfig : null,
            this.currentQueryConfig.entityTailConfigs, null, this.currentQueryConfig.rolloverToCash, null)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(x => {
                setTimeout(() => {
                    this.counterPartyCcrEceChartData = x.entityCounterPartyCcrAndEces;
                    this.wccrPeriodGridData = this.getWccrPeriods(this.counterPartyCcrEceChartData);
                }, 0);
            });
    }

    public getWccrPeriods(ccrEceData: any[]): any[] {
        const wccrPeriod = [];
        const startEnds = this.getStartAndEndWccrPeriods(ccrEceData);
        _.each(startEnds, (x: any, i: number) => {
            const obj = ccrEceData[x.index];
            if (i === 0 && x.type === 'end') {
                wccrPeriod.push(this.getNewWccrPeriod('', obj.occur, '', obj.ccr, obj.cashSfFormatted, obj.treasurySfFormatted));
            } else if (i === startEnds.length - 1 && x.type === 'start') {
                const lowestCcrRecord = this.getLowestCcrRecord(ccrEceData, x.index, ccrEceData.length - 1);
                wccrPeriod.push(this.getNewWccrPeriod(obj.occur, '', '', lowestCcrRecord.ccr, lowestCcrRecord.cashSfFormatted,
                    lowestCcrRecord.treasurySfFormatted));
            } else if (x.type === 'start' && startEnds[i + 1].type === 'end') {
                const endObj = ccrEceData[startEnds[i + 1].index];
                const lowestCcrRecord = this.getLowestCcrRecord(ccrEceData, x.index, startEnds[i + 1].index);
                wccrPeriod.push(this.getNewWccrPeriod(obj.occur, endObj.occur, (endObj.timeStep - obj.timeStep) + 'M',
                    lowestCcrRecord.ccr, lowestCcrRecord.cashSfFormatted, lowestCcrRecord.treasurySfFormatted));
            }
        });
        return wccrPeriod;
    }

    public getLowestCcrRecord(ccrEceData: any[], startIndex: number, endIndex: number): any {
        const list = ccrEceData.slice(startIndex, endIndex + 1);
        let min = null;
        let minIndex = 0;
        _.each(list, (x: any, i: number) => {
            if (min == null || x.ccr < min) {
                min = x.ccr;
                minIndex = i;
            }
        });
        return list[minIndex];
    }

    public getNewWccrPeriod(starts: string, ends: string, dur: string, ccr: string, cashEceInMUsd: string, bondEceInMUsd: string): any {
        return {
            start: starts,
            end: ends,
            duration: dur,
            ccrPercentageString: ccr,
            cashEceInMUsd: cashEceInMUsd,
            bondEceInMUsd: bondEceInMUsd
        };
    }

    public getStartAndEndWccrPeriods(ccrEceData: any[]): any[] {
        const startEnds = [];
        _.each(ccrEceData, (x: any, i: number) => {
            if (x.ccr < 150 && (i === 0 || (i > 0 && ccrEceData[i - 1].ccr >= 150))) {
                startEnds.push({type: 'start', index: i});
            }

            if (x.ccr < 150 && (i < ccrEceData.length - 1 && ccrEceData[i + 1].ccr >= 150)) {
                startEnds.push({type: 'end', index: i});
            }
        });
        return startEnds;
    }

    public getCounterPartyDerivativeAndCollateral() {
        this.dataService.getCounterPartyDerivativeAndCollateral(this.entityName, this.counterPartyName,
            this.currentQueryConfig.setForGroup ? this.currentQueryConfig.groupTailConfig : null,
            this.currentQueryConfig.entityTailConfigs, null, this.currentQueryConfig.rolloverToCash)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(x => {
                setTimeout(() => {
                    this.derivativesVsCollateralChartData = x.counterPartyDerivativeVsCollaterals;
                }, 0);
            });
    }

    public getInstruments() {
        this.showInstrumentGridOverlay();
        const wccrServiceRequest = <ServiceRequest>{
            groupLevelTail: this.currentQueryConfig.setForGroup ? this.currentQueryConfig.groupTailConfig : null,
            countryLevelTails: this.currentQueryConfig.entityTailConfigs,
            associatedLegalEntities: null,
            shockMtmValue: null,
            rolloverToCash: this.currentQueryConfig.rolloverToCash,
            instrumentGridFrom: this.instrumentGridToolBarOption.horizon.from,
            instrumentGridTo: this.instrumentGridToolBarOption.horizon.to,
            instrumentGridInstrumentType: this.instrumentGridToolBarOption.instrumentType,
            maturityDateFrom: this.ccrTimeStepClickFilter ? this.ccrTimeStepClickFilter.startDate : null,
            maturityDateTo: this.ccrTimeStepClickFilter ? this.ccrTimeStepClickFilter.endDate : null,
            entity: this.entityName,
            counterParty: this.counterPartyName

        };

        this.dataService.getEntityCounterPartyInstruments(wccrServiceRequest)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(x => {
                this.hideInstrumentGridOverlay();
                this.instrumentGridData = x.instrumentGridItems;
            }, error => {
                this.hideInstrumentGridOverlay();
                this.instrumentGridData = [];
                alert('Error Happened while trying to load "Instrument"...');
            });
    }

    onInstrumentGridReady(params) {
        this.instrumentGridApi = params.api;
        params.api.sizeColumnsToFit();
    }

    onWccrPeriodGridReady(params) {
        this.wccrPeriodGridApi = params.api;
        params.api.sizeColumnsToFit();
    }

    getNodeChildDetails(rowItem): any {
        if (rowItem.childInstruments) {
            return {
                group: true,
                expanded: false,
                children: rowItem.childInstruments,
                key: rowItem.type
            };
        } else {
            return null;
        }
    }

    getSimpleCellRenderer() {
        
        function SimpleCellRenderer() {
        }

        SimpleCellRenderer.prototype.init = function (params) {
            const tempDiv = document.createElement('div');
            if (params.node.group) {
                tempDiv.innerHTML =
                    '<span>' + params.value + '</span>';
            } else {
                tempDiv.innerHTML = '<span><span><i class="material-icons md-15">subdirectory_arrow_right</i></span> <span>'
                    + params.value + '</span></span>';
            }
            this.eGui = tempDiv.firstChild;
        };
        SimpleCellRenderer.prototype.getGui = function () {
            return this.eGui;
        };
        return SimpleCellRenderer;
    }

    onUpdateHorizonTimeRange(horizon: any) {
        this.instrumentGridToolBarOption.horizon.from = horizon.from;
        this.instrumentGridToolBarOption.horizon.to = horizon.to;
        this.getInstruments();
    }

    onInstrumentGridCollapseAll() {
        this.instrumentGridOptions.api.forEachNode(node => {
            if (node.group) {
                node.expanded = false;
            }
        });
        this.instrumentGridOptions.api.onGroupExpandedOrCollapsed();
    }

    onInstrumentGridExpandAll() {
        this.instrumentGridOptions.api.forEachNode(node => {
            if (node.group) {
                node.expanded = true;
            }
        });
        this.instrumentGridOptions.api.onGroupExpandedOrCollapsed();
    }

    selectedGridInstrumentTypeChange(instrumentType) {
        this.instrumentGridToolBarOption.instrumentType = instrumentType;
        this.getInstruments();
    }

    onCcrTimeStepClick(ccrChartClickFilter) {
        this.ccrTimeStepClickFilter = ccrChartClickFilter;
        this.getInstruments();
    }

    filterInstrumentsByCcrTimeStepClickData(ccrTimeStepData) {
        /*this.dataService.getExpiredBondsAtCcrTimeStep(ccrTimeStepData.startDate, ccrTimeStepData.endDate, ccrTimeStepData.counterParty,
            this.entityName)
            .subscribe(x => {
                this.instrumentGridData = x.instrumentGridItems;
            }, error => {
                alert('Error Happened while trying to load "Expired Bonds"...');
            });*/
    }

    removeExpiredBondFilter() {
        this.ccrTimeStepClickFilter = null;
        this.getInstruments();
    }

    public resetPageData() {
        this.counterPartyCcrEceChartData = null;
        this.derivativesVsCollateralChartData = null;
    }

    public showInstrumentGridOverlay() {
        if (this.instrumentGridApi) {
            this.instrumentGridApi.showLoadingOverlay();
        }
    }

    public hideInstrumentGridOverlay() {
        if (this.instrumentGridApi) {
            this.instrumentGridApi.hideOverlay();
        }
    }

    public showWccrPeriodGridOverlay() {
        if (this.wccrPeriodGridApi) {
            this.wccrPeriodGridApi.showLoadingOverlay();
        }
    }

    public hideWccrPeriodGridOverlay() {
        if (this.wccrPeriodGridApi) {
            this.wccrPeriodGridApi.hideOverlay();
        }
    }

    public subscribeOnPageReload() {
        this.appEvents.pageReload
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(x => {
                this.resetPageData();
                this.reloadPage();
            });
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

  
}
